import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@Auth/useAuth";
import { Entity } from "@models/Entity";
import { Location, LocationsClient } from "@Clients/LocationsClient";
import { locationKeys } from "./locationKeys";

export function useCreateLocation() {
  const queryClient = useQueryClient();
  const { baseUser } = useAuth();
  const orgId = baseUser?.organization_id;

  return useMutation({
    mutationFn: (input: Omit<Location, "id">) =>
      LocationsClient.createLocation(input),
    meta: { entity: Entity.LOCATIONS, action: "create" },
    onSuccess: () => {
      if (orgId) {
        queryClient.invalidateQueries({ queryKey: locationKeys.all(orgId) });
      }
    },
  });
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();
  const { baseUser } = useAuth();
  const orgId = baseUser?.organization_id;

  return useMutation({
    mutationFn: (input: Location) => LocationsClient.updateLocation(input),
    meta: { entity: Entity.LOCATIONS, action: "update" },
    onSuccess: () => {
      if (orgId) {
        queryClient.invalidateQueries({ queryKey: locationKeys.all(orgId) });
      }
    },
  });
}

/**
 * Delete a location. The `meta` lets the global mutation handler dispatch
 * to `locationErrorMapper` — a delete blocked by inventory (Postgres 23503)
 * surfaces "Cannot delete this location — it still holds inventory." without
 * the caller touching onErrorShow.
 */
export function useDeleteLocation() {
  const queryClient = useQueryClient();
  const { baseUser } = useAuth();
  const orgId = baseUser?.organization_id;

  return useMutation({
    mutationFn: (id: string) => LocationsClient.deleteLocation(id),
    meta: { entity: Entity.LOCATIONS, action: "delete" },
    onSuccess: () => {
      if (orgId) {
        queryClient.invalidateQueries({ queryKey: locationKeys.all(orgId) });
      }
    },
  });
}
